import { useQueryClient } from '@tanstack/react-query';
import { FC, useEffect, useState } from 'react';

import { useEventListener } from '@bonusly/events';

import { GiveBoxEvent } from '../../recognition/give-box/events';
import { useSeasonalTheme } from '../seasonal-theme-context';
import { useIsAdminRoute } from '../use-is-admin-route';
import BeachBg from './images/backgrounds/day.webp';
import BlueTube from './images/blue-tube.png';
import Cloud1 from './images/cloud-1.png';
import Cloud2 from './images/cloud-2.png';
import Cloud3 from './images/cloud-3.png';
import Cloud4 from './images/cloud-4.png';
import Cloud5 from './images/cloud-5.png';
import Flamingo from './images/flamingo.png';
import GreenTube from './images/green-tube.png';
import MagentaTube from './images/magenta-tube.png';
import OrangeTube from './images/orange-tube.png';
import Pineapple from './images/pineapple.png';
import PinkTube from './images/pink-tube.png';
import PurpleTube from './images/purple-tube.png';
import RedTube from './images/red-tube.png';
import Unicorn from './images/unicorn.png';
import YellowFloat from './images/yellow-float.png';
import { MoonSvg } from './moon-svg';
import { SunSvg } from './sun-svg';
import { summerGivenCountQueryKey, useSummerGivenCount } from './use-summer-given-count';

const TOYS = [
	// 0: Pineapple, Red float
	{ src: Pineapple, alt: 'Pineapple', top: '25.91%', left: '91.76%', width: '7.15vw', threshold: 0 },
	{ src: RedTube, alt: 'Red float', top: '52.26%', left: '26.91%', width: '9.46vw', threshold: 0 },

	// 1: Pink tube
	{ src: PinkTube, alt: 'Pink tube', top: '35.39%', left: '24.34%', width: '5.15vw', threshold: 1 },

	// 2: Green tube (right) and green tube (left)
	{ src: GreenTube, alt: 'Green tube (left)', top: '40.70%', left: '4.56%', width: '6.06vw', threshold: 2, id: 'green-tube-left' },
	{ src: GreenTube, alt: 'Green tube (right)', top: '31.49%', left: '82.15%', width: '4.50vw', threshold: 2, id: 'green-tube-right' },

	// 3: Flamingo and orange tube
	{ src: Flamingo, alt: 'Flamingo', top: '29.61%', left: '10%', width: '9.5625vw', threshold: 3 },
	{ src: OrangeTube, alt: 'Orange tube', top: '28.53%', left: '72.03%', width: '3.49vw', threshold: 3 },

	// 4: Blue tube and yellow float
	{ src: BlueTube, alt: 'Blue tube', top: '49.72%', left: '86%', width: '8.46875vw', threshold: 4 },
	{ src: YellowFloat, alt: 'Yellow float', top: '40.22%', left: '56.03%', width: '13.65vw', threshold: 4 },

	// 5: Magenta tube and purple tube
	{ src: MagentaTube, alt: 'Magenta tube', top: '46.90%', left: '70.94%', width: '8.09vw', threshold: 5 },
	{ src: PurpleTube, alt: 'Purple tube', top: '28.38%', left: '21.09%', width: '3.26vw', threshold: 5 },

	// last: Unicorn
	{ src: Unicorn, alt: 'Unicorn', top: '24.73%', left: '75.31%', width: '11.80vw', threshold: 6 },
];

type CelestialBody = 'sun' | 'moon';

const STARS = Array.from({ length: 40 }, (_, i) => ({
	x: ((i * 7919 + 1301) % 97) + 1,
	y: ((i * 6271 + 883) % 93) + 2,
	size: 1.5 + ((i * 3571) % 3),
	delay: ((i * 4219) % 5000) / 1000,
}));

const getSceneState = (date: Date) => {
	const hour = date.getHours() + date.getMinutes() / 60;

	const isDay = hour >= 5 && hour < 19;

	let progress: number;
	if (isDay) {
		progress = (hour - 5) / 14;
	} else {
		const nightHour = hour >= 19 ? hour - 19 : hour + 5;
		progress = nightHour / 10;
	}

	const x = 10 + progress * 80;
	const yBase = 85;
	const yPeak = 15;
	const y = yBase - (yBase - yPeak) * (1 - Math.pow(2 * progress - 1, 2));

	let skyGlow: string;
	let skyGlowSize: number;
	let skyGlowOpacity: number;
	let bodyGlow: string;
	let bodyGlowOpacity: number;
	let bodySize: number;
	let bodyColor: string;
	let bodyType: CelestialBody;
	let bodyFilter: string | undefined;
	let bodyOpacity: number;
	let starsOpacity: number;
	let beachFilter: string;
	let skyTint: string;
	let skyTintOpacity: number;
	let cloudsX: number | null;

	if (hour >= 4 && hour < 5) {
		skyGlow = 'radial-gradient(ellipse, rgba(165, 180, 252, 0.1) 0%, transparent 55%)';
		skyGlowSize = 600;
		skyGlowOpacity = 0.2;
		bodyGlow = 'radial-gradient(circle, rgba(199, 210, 254, 0.1) 0%, transparent 60%)';
		bodyGlowOpacity = 0.1;
		bodySize = 108;
		bodyColor = '#a5b4fc';
		bodyType = 'moon';
		bodyOpacity = 0.1;
		starsOpacity = 0.3;
		beachFilter = 'brightness(0.35) contrast(1.1) hue-rotate(30deg) saturate(0.5)';
		skyTint = '#0f0b2a';
		skyTintOpacity = 0.85;
	} else if (hour >= 5 && hour < 7) {
		skyGlow = 'radial-gradient(ellipse, rgba(253, 230, 138, 0.5) 0%, rgba(252, 165, 165, 0.15) 40%, rgba(252, 165, 165, 0.03) 70%, transparent 90%)';
		skyGlowSize = 900;
		skyGlowOpacity = 0.8;
		bodyGlow = 'radial-gradient(circle, rgba(253, 230, 138, 0.4) 0%, rgba(253, 230, 138, 0.15) 35%, rgba(253, 230, 138, 0.04) 65%, transparent 100%)';
		bodyGlowOpacity = 0.7;
		bodySize = 150;
		bodyColor = '#fde68a';
		bodyType = 'sun';
		bodyFilter = 'hue-rotate(-25deg) saturate(1.6)';
		bodyOpacity = 0.15;
		starsOpacity = 0;
		beachFilter = 'brightness(0.7) contrast(1.0) hue-rotate(15deg) saturate(0.8)';
		skyTint = '#4338ca';
		skyTintOpacity = 0.3;
	} else if (hour >= 7 && hour < 11) {
		skyGlow = 'radial-gradient(ellipse, rgba(253, 224, 71, 0.3) 0%, rgba(186, 230, 253, 0.08) 50%, rgba(186, 230, 253, 0.02) 70%, transparent 90%)';
		skyGlowSize = 1000;
		skyGlowOpacity = 0.6;
		bodyGlow = 'radial-gradient(circle, rgba(253, 224, 71, 0.3) 0%, rgba(253, 224, 71, 0.1) 35%, rgba(253, 224, 71, 0.02) 65%, transparent 100%)';
		bodyGlowOpacity = 0.55;
		bodySize = 168;
		bodyColor = '#fcd34d';
		bodyType = 'sun';
		bodyOpacity = 0.7;
		starsOpacity = 0;
		beachFilter = 'brightness(1) saturate(1)';
		skyTint = '#3b82f6';
		skyTintOpacity = 0;
	} else if (hour >= 11 && hour < 14) {
		skyGlow = 'radial-gradient(ellipse, rgba(254, 249, 195, 0.3) 0%, rgba(186, 230, 253, 0.06) 50%, rgba(186, 230, 253, 0.01) 70%, transparent 90%)';
		skyGlowSize = 1200;
		skyGlowOpacity = 0.5;
		bodyGlow = 'radial-gradient(circle, rgba(250, 204, 21, 0.25) 0%, rgba(250, 204, 21, 0.08) 35%, rgba(250, 204, 21, 0.02) 65%, transparent 100%)';
		bodyGlowOpacity = 0.45;
		bodySize = 180;
		bodyColor = '#facc15';
		bodyType = 'sun';
		bodyOpacity = 0.85;
		starsOpacity = 0;
		beachFilter = 'brightness(1) saturate(1)';
		skyTint = '#3b82f6';
		skyTintOpacity = 0;
	} else if (hour >= 14 && hour < 17) {
		skyGlow = 'radial-gradient(ellipse, rgba(253, 186, 116, 0.3) 0%, rgba(253, 230, 138, 0.08) 45%, rgba(253, 230, 138, 0.02) 70%, transparent 90%)';
		skyGlowSize = 1000;
		skyGlowOpacity = 0.6;
		bodyGlow = 'radial-gradient(circle, rgba(253, 186, 116, 0.3) 0%, rgba(253, 186, 116, 0.1) 35%, rgba(253, 186, 116, 0.02) 65%, transparent 100%)';
		bodyGlowOpacity = 0.6;
		bodySize = 162;
		bodyColor = '#fdba74';
		bodyType = 'sun';
		bodyOpacity = 0.7;
		starsOpacity = 0;
		beachFilter = 'brightness(0.95) contrast(1.0) hue-rotate(5deg) saturate(0.95)';
		skyTint = '#3b82f6';
		skyTintOpacity = 0;
	} else if (hour >= 17 && hour < 18) {
		skyGlow = 'radial-gradient(ellipse, rgba(251, 146, 60, 0.4) 0%, rgba(253, 164, 175, 0.12) 40%, rgba(253, 164, 175, 0.03) 70%, transparent 90%)';
		skyGlowSize = 900;
		skyGlowOpacity = 0.75;
		bodyGlow = 'radial-gradient(circle, rgba(253, 164, 175, 0.35) 0%, rgba(253, 164, 175, 0.12) 35%, rgba(253, 164, 175, 0.03) 65%, transparent 100%)';
		bodyGlowOpacity = 0.6;
		bodySize = 144;
		bodyColor = '#fb923c';
		bodyType = 'sun';
		bodyFilter = 'hue-rotate(-35deg) saturate(1.8)';
		bodyOpacity = 0.55;
		starsOpacity = 0.15;
		beachFilter = 'brightness(0.75) contrast(1.05) hue-rotate(15deg) saturate(0.85)';
		skyTint = '#1e1b4b';
		skyTintOpacity = 0.35;
	} else if (hour >= 18 && hour < 19) {
		skyGlow = 'radial-gradient(ellipse, rgba(251, 146, 60, 0.15) 0%, transparent 55%)';
		skyGlowSize = 800;
		skyGlowOpacity = 0.3;
		bodyGlow = 'radial-gradient(circle, rgba(253, 164, 175, 0.1) 0%, transparent 60%)';
		bodyGlowOpacity = 0.1;
		bodySize = 136;
		bodyColor = '#fb923c';
		bodyType = 'sun';
		bodyFilter = 'hue-rotate(-35deg) saturate(1.8)';
		bodyOpacity = 0.1;
		starsOpacity = 0.3;
		beachFilter = 'brightness(0.55) contrast(1.1) hue-rotate(25deg) saturate(0.7)';
		skyTint = '#0f0b2a';
		skyTintOpacity = 0.6;
	} else if (hour >= 19 && hour < 20) {
		skyGlow = 'radial-gradient(ellipse, rgba(199, 210, 254, 0.1) 0%, transparent 55%)';
		skyGlowSize = 700;
		skyGlowOpacity = 0.3;
		bodyGlow = 'radial-gradient(circle, rgba(199, 210, 254, 0.1) 0%, transparent 60%)';
		bodyGlowOpacity = 0.15;
		bodySize = 120;
		bodyColor = '#c7d2fe';
		bodyType = 'moon';
		bodyOpacity = 0.15;
		starsOpacity = 0.4;
		beachFilter = 'brightness(0.4) contrast(1.15) hue-rotate(30deg) saturate(0.6)';
		skyTint = '#0f0b2a';
		skyTintOpacity = 0.75;
	} else if (hour >= 20 && hour < 21) {
		skyGlow = 'radial-gradient(ellipse, rgba(199, 210, 254, 0.2), transparent 60%)';
		skyGlowSize = 700;
		skyGlowOpacity = 0.4;
		bodyGlow = 'radial-gradient(circle, rgba(199, 210, 254, 0.15) 0%, rgba(199, 210, 254, 0.05) 40%, transparent 80%)';
		bodyGlowOpacity = 0.35;
		bodySize = 120;
		bodyColor = '#c7d2fe';
		bodyType = 'moon';
		bodyOpacity = 0.5;
		starsOpacity = 0.6;
		beachFilter = 'brightness(0.3) contrast(1.2) hue-rotate(30deg) saturate(0.5)';
		skyTint = '#0f0b2a';
		skyTintOpacity = 0.85;
	} else {
		skyGlow = 'radial-gradient(ellipse, rgba(165, 180, 252, 0.15), transparent 55%)';
		skyGlowSize = 600;
		skyGlowOpacity = 0.3;
		bodyGlow = 'radial-gradient(circle 200px, rgba(199, 210, 254, 0.18), transparent)';
		bodyGlowOpacity = 0.25;
		bodySize = 108;
		bodyColor = '#a5b4fc';
		bodyType = 'moon';
		bodyOpacity = 0.65;
		starsOpacity = 0.7;
		beachFilter = 'brightness(0.25) contrast(1.2) hue-rotate(30deg) saturate(0.45)';
		skyTint = '#0f0b2a';
		skyTintOpacity = 0.9;
	}

	if (hour >= 7 && hour < 11) {
		cloudsX = (1 - (hour - 7) / 4) * 100;
	} else if (hour >= 11 && hour < 14) {
		cloudsX = -((hour - 11) / 3) * 100;
	} else {
		cloudsX = null;
	}

	const altitude = (y - yPeak) / (yBase - yPeak);
	bodySize = bodySize * (0.5 + 0.5 * altitude);

	const nightProgress = isDay ? 0 : progress;
	const starsDriftX = nightProgress * 12 - 6;
	const starsDriftY = -(nightProgress * 6 - 3);

	return {
		skyGlow,
		skyGlowSize,
		skyGlowOpacity,
		bodyGlow,
		bodyGlowOpacity,
		x,
		y,
		bodySize,
		bodyColor,
		bodyType,
		bodyFilter,
		bodyOpacity,
		starsOpacity,
		starsDriftX,
		starsDriftY,
		beachFilter,
		skyTint,
		skyTintOpacity,
		isDay,
		cloudsX,
	};
};

export const DynamicBackground: FC = () => {
	const isAdminRoute = useIsAdminRoute();
	const queryClient = useQueryClient();
	const { enableDate } = useSeasonalTheme();
	const { data: givenCount } = useSummerGivenCount();
	const [scene, setScene] = useState(() => getSceneState(new Date()));
	const [ready, setReady] = useState(false);

	useEventListener(GiveBoxEvent.Created, () => {
		queryClient.invalidateQueries(summerGivenCountQueryKey(enableDate));
	});

	useEffect(() => {
		requestAnimationFrame(() => setReady(true));

		const updateTime = () => setScene(getSceneState(new Date()));

		const timeInterval = window.setInterval(updateTime, 60_000);

		return () => {
			clearInterval(timeInterval);
		};
	}, []);

	if (isAdminRoute) return null;

	return (
		<>
			<style>
				{`
					html.summer-theme,
					html.summer-theme body {
						background: transparent !important;
					}

					.summer-theme [data-testid="recognition-content-wrapper"],
					.summer-theme [data-testid="homepage-content-wrapper"],
					.summer-theme [data-testid="right-content-wrapper"],
					.summer-theme [data-testid="left-sidebar-wrapper"],
					.summer-theme [data-testid="right-content-centerer"],
					.summer-theme [data-testid="main-feed-column"],
					.summer-theme [data-component="recap-page-layout"],
					.summer-theme #nav-wrapper,
					.summer-theme #root > div {
						background: transparent !important;
						background-color: transparent !important;
					}

					@keyframes summer-star-twinkle {
						0%, 100% { opacity: 0.3; transform: scale(1); }
						50% { opacity: 1; transform: scale(1.3); }
					}

					@keyframes summer-bob {
						0%, 100% { transform: translateY(0); }
						50% { transform: translateY(0.6vw); }
					}

					@media (prefers-reduced-motion: reduce) {
						.summer-beach-toy {
							animation: none !important;
						}
					}
				`}
			</style>

			<div
				style={{
					position: 'fixed',
					top: 0,
					left: 0,
					width: '100vw',
					height: '100vh',
					zIndex: -1,
					pointerEvents: 'none',
					opacity: ready ? 1 : 0,
					transition: 'opacity 0.3s ease',
					overflow: 'hidden',
					willChange: 'transform',
				}}
			>
				<div
					style={{
						position: 'absolute',
						top: 0,
						left: 0,
						width: '100%',
						height: '100%',
						background: 'linear-gradient(180deg, #0274E8 0%, #3EB2F8 100%)',
						transition: 'background 2s ease, opacity 2s ease',
					}}
				/>

				<div
					style={{
						position: 'absolute',
						top: 0,
						left: 0,
						width: '100%',
						height: '100%',
						backgroundColor: scene.skyTint,
						opacity: scene.skyTintOpacity,
						transition: 'opacity 2s ease, background-color 2s ease',
					}}
				/>

				<div
					style={{
						position: 'absolute',
						left: `${scene.x}%`,
						top: `${scene.y}%`,
						width: `${scene.skyGlowSize}px`,
						height: `${scene.skyGlowSize}px`,
						transform: 'translate(-50%, -50%)',
						borderRadius: '50%',
						background: scene.skyGlow,
						opacity: scene.skyGlowOpacity,
						transition: 'left 2s ease, top 2s ease, width 2s ease, height 2s ease, opacity 2s ease, background 2s ease',
					}}
				/>

				<div
					style={{
						position: 'absolute',
						left: `${scene.x}%`,
						top: `${scene.y}%`,
						width: '400px',
						height: '400px',
						transform: 'translate(-50%, -50%)',
						borderRadius: '50%',
						background: scene.bodyGlow,
						opacity: scene.bodyGlowOpacity,
						transition: 'left 2s ease, top 2s ease, opacity 2s ease, background 2s ease',
					}}
				/>

				<div
					style={{
						position: 'absolute',
						top: 0,
						left: 0,
						width: '100%',
						height: '100%',
						opacity: scene.starsOpacity,
						transform: `translate(${scene.starsDriftX}%, ${scene.starsDriftY}%)`,
						transition: 'opacity 3s ease, transform 2s ease',
					}}
				>
					{STARS.map((star, i) => (
						<div
							key={i}
							style={{
								position: 'absolute',
								left: `${star.x}%`,
								top: `${star.y}%`,
								width: `${star.size}px`,
								height: `${star.size}px`,
								borderRadius: '50%',
								backgroundColor: '#e0e7ff',
								boxShadow: `0 0 ${star.size + 2}px rgba(224, 231, 255, 0.6)`,
								animation: `summer-star-twinkle ${2.5 + star.delay}s ease-in-out ${star.delay}s infinite`,
							}}
						/>
					))}
				</div>

				<div
					style={{
						position: 'absolute',
						left: `${scene.x}%`,
						top: `${scene.y}%`,
						transform: 'translate(-50%, -50%)',
						opacity: scene.bodyOpacity,
						transition: 'left 2s ease, top 2s ease, opacity 2s ease, filter 2s ease',
						filter: `drop-shadow(0 0 20px ${scene.bodyColor})`,
					}}
				>
					{scene.bodyType === 'sun' ? <SunSvg size={scene.bodySize} filter={scene.bodyFilter} /> : <MoonSvg size={scene.bodySize} />}
				</div>

				<img
					src={BeachBg}
					alt=""
					style={{
						position: 'absolute',
						bottom: 0,
						left: 0,
						width: '100%',
						filter: scene.beachFilter,
						transition: 'filter 2s ease',
						color: 'transparent',
						pointerEvents: 'none',
					}}
				/>

				{/****** CLOUDS ******/}

				{scene.cloudsX !== null && (
					<div
						style={{
							position: 'absolute',
							top: 0,
							left: 0,
							width: '100%',
							bottom: '22.375vw',
							pointerEvents: 'none',
							transform: `translateX(${scene.cloudsX}%)`,
							transition: 'transform 60s linear',
						}}
					>
						<img
							src={Cloud1}
							alt=""
							style={{
								position: 'absolute',
								bottom: '4%',
								left: '5.75%',
								width: '45.125vw',
								pointerEvents: 'none',
							}}
						/>
						<img
							src={Cloud2}
							alt=""
							style={{
								position: 'absolute',
								bottom: '16%',
								left: '47.75%',
								width: '8.8vw',
								pointerEvents: 'none',
							}}
						/>
						<img
							src={Cloud3}
							alt=""
							style={{
								position: 'absolute',
								bottom: '3.75%',
								left: '54.75%',
								width: '31.96vw',
								pointerEvents: 'none',
							}}
						/>
						<img
							src={Cloud4}
							alt=""
							style={{
								position: 'absolute',
								bottom: '60.75%',
								left: '73.75%',
								width: '10.69vw',
								pointerEvents: 'none',
							}}
						/>
						<img
							src={Cloud5}
							alt=""
							style={{
								position: 'absolute',
								bottom: '30.75%',
								left: '86.75%',
								width: '10.75vw',
								pointerEvents: 'none',
							}}
						/>
					</div>
				)}

				{/****** BEACH TOYS ******/}

				<div
					style={{
						position: 'absolute',
						bottom: 0,
						left: 0,
						width: '100%',
						height: '22.375vw',
						pointerEvents: 'none',
					}}
				>
					{givenCount !== undefined &&
						TOYS.filter((toy) => givenCount >= toy.threshold).map((toy, idx) => (
							<img
								key={toy.id || toy.alt || idx}
								src={toy.src}
								alt=""
								className="summer-beach-toy"
								style={{
									position: 'absolute',
									top: toy.top,
									left: toy.left,
									width: toy.width,
									pointerEvents: 'none',
									animation: `summer-bob ${5 + (idx % 3)}s ease-in-out ${idx * 0.4}s 12`,
								}}
							/>
						))}
				</div>
			</div>
		</>
	);
};
